import { motion } from "framer-motion";
import { Droplets, Recycle, ShieldCheck, ArrowRight } from "lucide-react";
import { DemoBar } from "@/components/DemoBar";
import { CASES } from "@/data/cases";

const img = CASES.find((c) => c.slug === "kede-water").image;

const BENEFITS = [
  { icon: Droplets, title: "Kilde-rent vand", text: "Filtreret og testet — hvert batch dokumenteret i vores åbne kvalitetslog." },
  { icon: ShieldCheck, title: "Mineralbalance", text: "Naturligt indhold af mineraler, tilpasset en aktiv og sund hverdag." },
  { icon: Recycle, title: "Genbrugsflasker", text: "Flasken er 100% genanvendt plast — og kan pantes overalt." },
];

export default function KedeWaterDemo() {
  return (
    <div data-testid="demo-kede-water" className="min-h-screen bg-[#F2F8FB] text-[#0C2530]">
      <DemoBar name="KEDE-Water" />

      <header className="flex items-center justify-between border-b border-[#0C2530]/10 bg-white/60 px-6 py-5 backdrop-blur-md md:px-12">
        <p className="font-syne text-xl font-extrabold tracking-tight">
          KEDE<span className="text-[#1E88B8]">-Water</span>
        </p>
        <p className="hidden text-xs uppercase tracking-[0.25em] text-[#0C2530]/50 md:block">
          En del af KEDE Health Platform
        </p>
        <button data-testid="kede-water-order-button" className="rounded-full bg-[#1E88B8] px-6 py-2.5 text-sm font-bold text-white transition-transform duration-300 hover:scale-105">
          Bestil vand
        </button>
      </header>

      <section className="grid grid-cols-1 items-center gap-12 px-6 py-16 md:grid-cols-2 md:px-12 md:py-24">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="flex items-center gap-2 text-sm uppercase tracking-[0.3em] text-[#1E88B8]">
            <Droplets className="h-4 w-4" /> Sundhed starter med vand
          </p>
          <h1 className="mt-4 font-syne text-5xl font-extrabold uppercase leading-[0.92] tracking-tighter md:text-7xl">
            Rent vand.<br /><span className="text-[#1E88B8]">Hver gang.</span>
          </h1>
          <p className="mt-6 max-w-md text-[#0C2530]/70">
            KEDE-Water leverer dokumenteret rent kildevand til hjem og kontorer —
            med abonnement, så du aldrig løber tør.
          </p>
          <button data-testid="kede-water-hero-cta" className="group mt-10 flex w-fit items-center gap-2 rounded-full bg-[#1E88B8] px-8 py-4 text-sm font-bold text-white transition-transform duration-300 hover:scale-105">
            Start abonnement <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="overflow-hidden rounded-[2rem] bg-white shadow-2xl shadow-[#1E88B8]/15"
        >
          <img src={img} alt="KEDE-Water flaske" data-testid="kede-water-hero-image" className="aspect-[4/3] w-full object-cover" />
        </motion.div>
      </section>

      <section className="bg-white px-6 py-20 md:px-12">
        <h2 className="font-syne text-3xl font-extrabold uppercase tracking-tighter md:text-5xl">Derfor KEDE-Water</h2>
        <div data-testid="kede-water-benefits" className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="rounded-2xl border border-[#0C2530]/10 bg-[#F2F8FB] p-8 transition-transform duration-300 hover:-translate-y-1"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1E88B8]/10">
                  <Icon className="h-6 w-6 text-[#1E88B8]" />
                </span>
                <h3 className="mt-5 font-syne text-xl font-bold">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#0C2530]/70">{b.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-[#0C2530]/10 px-6 py-10 text-center text-sm text-[#0C2530]/60 md:px-12">
        KEDE-Water · En del af KEDE Health Platform · hej@kede-water.dk
      </footer>
    </div>
  );
}
